import React, { useState, useMemo, memo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const CaptainSelection = memo(({ players, selectedCaptain, onCaptainSelect }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [tempSelection, setTempSelection] = useState(null);

  // Memoize player options to prevent recalculation on every render
  const playerOptions = useMemo(() => {
    return players?.map(player => ({
      value: player?.id,
      label: `${player?.name} - ${player?.role}`,
      description: `DOB: ${player?.dob} | MCA ID: ${player?.mcaId}`
    })) || [];
  }, [players]);

  const handleSelectionChange = (value) => {
    const player = players?.find(p => p?.id === value);
    setTempSelection(player);
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    onCaptainSelect(tempSelection);
    setShowConfirmation(false);
    setTempSelection(null);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setTempSelection(null);
  };

  // Memoize current captain to prevent recalculation
  const currentCaptain = useMemo(() => {
    return players?.find(p => p?.id === selectedCaptain);
  }, [players, selectedCaptain]);

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <Icon name="Award" size={20} color="var(--color-primary)" />
        Select Team Captain
      </h3>
      <p className="text-sm text-muted-foreground mb-6">
        Choose a player from your registered team to be the captain. The captain will be highlighted in all team documents and communications.
      </p>
      {/* Current Captain Display */}
      {currentCaptain && !showConfirmation && (
        <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {currentCaptain?.name?.charAt(0)?.toUpperCase() || 'C'}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-foreground">{currentCaptain?.name}</h4>
                <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs font-medium rounded">
                  Captain
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{currentCaptain?.role}</p>
              <p className="text-xs text-muted-foreground mt-1">
                DOB: {currentCaptain?.dob} | MCA ID: {currentCaptain?.mcaId}
              </p>
            </div>

            <Icon name="CheckCircle" size={24} color="var(--color-success)" />
          </div>
        </div>
      )}
      {/* Captain Selection Dropdown */}
      <Select
        label="Choose Captain"
        description="Select from registered players"
        options={playerOptions}
        value={selectedCaptain || ''}
        onChange={handleSelectionChange}
        searchable
        required
        placeholder="Select a player as captain"
      />
      {/* Confirmation Modal */}
      {showConfirmation && tempSelection && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-lg max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="AlertCircle" size={24} color="var(--color-warning)" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  Confirm Captain Selection
                </h4>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to select <span className="font-medium text-foreground">{tempSelection?.name}</span> as the team captain?
                </p>
              </div>
            </div>

            {/* Selected Player Preview */}
            <div className="mb-6 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {tempSelection?.name?.charAt(0)?.toUpperCase() || 'P'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground">{tempSelection?.name}</p>
                  <p className="text-sm text-muted-foreground">{tempSelection?.role}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleConfirm}
                fullWidth
                iconName="Check"
                iconPosition="left"
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Captain Responsibilities */}
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
          <Icon name="Info" size={16} />
          Captain Responsibilities
        </h4>
        <ul className="text-sm text-muted-foreground space-y-2">
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-primary)" />
            <span>Lead the team during matches and practice sessions</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-primary)" />
            <span>Represent the team at toss and official ceremonies</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-primary)" />
            <span>Coordinate with coaches and tournament officials</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-primary)" />
            <span>Ensure team discipline and sportsmanship</span>
          </li>
        </ul>
      </div>
    </div>
  );
});

CaptainSelection.displayName = 'CaptainSelection';

export default CaptainSelection;